import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import SuccessModal from "../components/SuccessModal";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Avatar,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  MoreVert,
  TextFields,
} from '@mui/icons-material';

function Support() {
  const [tabIndex, setTabIndex] = useState(0);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [successModal, setSuccessModal] = useState({
    open: false,
    title: "",
    message: ""
  });

  useEffect(() => {

    if (authLoading) return;

    if (!user) return;

    if (user.role === "super_admin") {
      fetchAllQueries();
      setTabIndex(1);
    } else {
      fetchUserQueries(user.id);
    }

  }, [user, authLoading]);

  const fetchAllQueries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("https://api.exihire.com/api/support-queries", {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Accept': 'application/json'
        }
      });
        if (response.ok) {
          const data = await response.json();
          const uiQueries = data.map(q => ({
            ...q,
            date: (q.createdAt || q.created_at) 
              ? new Date(q.createdAt || q.created_at).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0]
          }));
          setQueries(uiQueries);
        } else {
          console.error(`[DEBUG_LOG] fetchAllQueries failed: ${response.status} ${response.statusText}`);
          const text = await response.text();
          console.error(`Response body: ${text.substring(0, 200)}`);
        }
    } catch (error) {
      console.error("Error fetching all queries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserQueries = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      
      const response = await fetch(`https://api.exihire.com/api/support-queries/user/${userId}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Accept': 'application/json'
        }
      });
        if (response.ok) {
          const data = await response.json();
          const uiQueries = data.map(q => ({
            ...q,
            date: (q.createdAt || q.created_at)
              ? new Date(q.createdAt || q.created_at).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0]
          }));
          setQueries(uiQueries);
        } else {
          console.error(`[DEBUG_LOG] fetchUserQueries failed: ${response.status} ${response.statusText}`);
          const text = await response.text();
          console.error(`Response body: ${text.substring(0, 200)}`);
        }
    } catch (error) {
      console.error("Error fetching queries:", error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(queries.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedQueries = queries.slice((page - 1) * pageSize, page * pageSize);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleOpenReply = (query) => {
    setSelectedQuery(query);
    setReplyMessage("");
    setReplyDialogOpen(true);
  };

  const handleCloseReply = () => {
    setReplyDialogOpen(false);
    setSelectedQuery(null);
    setReplyMessage("");
  };

  const handleSendReply = async () => {
    if (!replyMessage || !selectedQuery) return;

    setSubmittingReply(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`https://api.exihire.com/api/support-queries/${selectedQuery.id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          replyMessage
        }),
      });

      if (response.ok) {
        setSuccessModal({
          open: true,
          title: "Message Sent Successfully",
          message: "Your reply has been sent to the user."
        });
        handleCloseReply();
        fetchAllQueries(); // Refresh list to show resolved status
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to send reply");
      }
    } catch (error) {
      console.error("Error sending reply:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const subject = e.target.subject.value;
    const message = e.target.message.value;

    if (subject && message) {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch("https://api.exihire.com/api/support-queries", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify({
            subject,
            message,
            userId: user ? user.id : null,
          }),
        });

        if (response.ok) {
          const newQuery = await response.json();
          // Adjust for UI display (backend uses createdAt/created_at, UI used date)
          const uiQuery = {
            ...newQuery,
            date: (newQuery.createdAt || newQuery.created_at)
              ? new Date(newQuery.createdAt || newQuery.created_at).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0]
          };
          setQueries([uiQuery, ...queries]);
          e.target.reset();
          setSuccessModal({
            open: true,
            title: "Message Sent Successfully",
            message: "Your support ticket has been submitted successfully. We will get back to you soon."
          });
          setTabIndex(1); // switch to View Queries
        } else {
          // Try to parse error as JSON, fallback to status text
          let errorMessage = "Failed to submit ticket. Please try again.";
          try {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
            } else {
              const text = await response.text();
              console.error("Non-JSON error response:", text);
            }
          } catch (e) {
            console.error("Error parsing error response:", e);
          }
          alert(errorMessage);
        }
      } catch (error) {
        console.error("Error submitting support ticket:", error);
        alert("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container sx={{ py: 6 }}>
      <Typography
        variant="h3"
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          color: "#4338CA",
          mb: 4,
        }}
      >
        Contact Support
      </Typography>

      {/* Centered Tabs */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered>
          {user?.role !== 'super_admin' && <Tab label="Raise a Ticket" />}
          <Tab label={user?.role === 'super_admin' ? "All Queries" : "View Tickets"} />
        </Tabs>
      </Box>

      {/* Raise a Ticket */}
      {tabIndex === 0 && user?.role !== 'super_admin' && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Paper
            sx={{
              p: 4,
              width: "100%",
              maxWidth: 600,
              borderRadius: '22px',
              boxShadow: '0 10px 40px -10px rgba(79, 70, 229, 0.2), 0 0 20px rgba(96, 165, 250, 0.15)',
              border: '1px solid #4F46E5',
              transition: 'box-shadow 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 15px 50px -10px rgba(79, 70, 229, 0.3), 0 0 25px rgba(96, 165, 250, 0.2)',
              }
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#0f172a' }}>
              Send us a message
            </Typography>
            <Box
              component="form"
              noValidate
              sx={{ mt: 2 }}
              onSubmit={handleSubmit}
            >
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                margin="normal"
              />
              <TextField
                fullWidth
                label="Message"
                name="message"
                multiline
                rows={4}
                margin="normal"
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Ticket"}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* View Queries */}
      {(user?.role === 'super_admin' ? tabIndex === 0 : tabIndex === 1) && (
        <Grid container spacing={3} sx={{ justifyContent: "center" }}>
          {queries.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4F46E5', mb: 1 }}>
                No Support Tickets Found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You have not raised any support tickets yet.
              </Typography>
            </Box>
          ) : (
            <>
              {paginatedQueries.map((query) => (
                <Grid item xs={12} md={6} key={query.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '22px',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.25s ease',
                      boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 28px rgba(15,23,42,0.10)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: '5px',
                        background: 'linear-gradient(90deg, #4F46E5 0%, #60A5FA 100%)',
                        borderTopLeftRadius: '22px',
                        borderTopRightRadius: '22px',
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 2.2, '&:last-child': { pb: 2.2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'rgba(79, 70, 229, 0.1)',
                              color: '#4F46E5',
                              width: 42,
                              height: 42,
                            }}
                          >
                            <TextFields fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 800,
                                color: '#0f172a',
                                fontSize: '1.2rem',
                                mb: 0.5
                              }}
                            >
                              {query.subject}
                            </Typography>
                            <Chip
                              label={query.status}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                bgcolor: (query.status === "Resolved" || query.status === "resolved") ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                color: (query.status === "Resolved" || query.status === "resolved") ? '#166534' : '#92400e'
                              }}
                            />
                          </Box>
                        </Box>
                        <Box>
                          {user?.role === 'super_admin' && (
                            <Tooltip title="Actions">
                              <IconButton onClick={() => handleOpenReply(query)} disabled={query.status === 'resolved' || query.status === 'Resolved'}>
                                <MoreVert />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {user?.role === 'super_admin' && query.user_email && (
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#4F46E5', fontWeight: 600 }}>
                          From: {query.user_email}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 3,
                          p: 1.75,
                          bgcolor: '#f8fafc',
                          mb: 3,
                          minHeight: 80,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.7,
                          }}
                        >
                          {query.message}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Submitted on
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {query.date}
                          </Typography>
                        </Box>
                        {user?.role === 'super_admin' && query.user_email && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            href={`mailto:${query.user_email}?subject=Re: ${query.subject}`}
                            sx={{ 
                              textTransform: 'none', 
                              borderRadius: '8px',
                              fontWeight: 'bold',
                              borderColor: '#4F46E5',
                              color: '#4F46E5'
                            }}
                          >
                            Mail
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={handleCloseReply} maxWidth="sm" fullWidth>
        <DialogTitle>Reply to Query</DialogTitle>
        <DialogContent dividers>
          {selectedQuery && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Subject:</Typography>
              <Typography variant="body1" gutterBottom>{selectedQuery.subject}</Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Original Message:</Typography>
              <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>{selectedQuery.message}</Typography>
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Your Reply"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReply}>Cancel</Button>
          <Button 
            onClick={handleSendReply} 
            variant="contained" 
            disabled={submittingReply || !replyMessage}
          >
            {submittingReply ? "Sending..." : "Send Reply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <SuccessModal
        open={successModal.open}
        handleClose={() => setSuccessModal({ ...successModal, open: false })}
        title={successModal.title}
        message={successModal.message}
      />
    </Container>
  );
}

export default Support;
